if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this._loaded = false;
    }

    connectedCallback() {
      if (!this._loaded) {
        this.performRecommendations();
      } else {
        this._syncAddedStates();
      }
    }

    /**
     * When removed from DOM (cart drawer innerHTML replacement),
     * save reference and restore on next frame so carousel persists.
     */
    disconnectedCallback() {
      if (!this._loaded) return;

      ProductRecommendations._savedInstance = this;

      var self = this;
      requestAnimationFrame(function() {
        if (ProductRecommendations._savedInstance !== self) return;
        if (document.body.contains(self)) return;

        var body = document.getElementById('CartDrawer-Body');
        if (!body) {
          ProductRecommendations._savedInstance = null;
          return;
        }

        // If the cart is now empty, do NOT restore.
        var emptyState = body.querySelector('.cart-drawer__empty');
        if (emptyState) {
          ProductRecommendations._savedInstance = null;
          return;
        }

        var freshRecs = body.querySelector('product-recommendations');

        if (freshRecs && freshRecs !== self) {
          // Check if cart contents changed (new/different product IDs)
          var oldIds = (self.dataset.allProductIds || '').split(',').filter(Boolean);
          var newIds = (freshRecs.dataset.allProductIds || '').split(',').filter(Boolean);

          // Copy updated data attributes
          self.dataset.allProductIds = freshRecs.dataset.allProductIds || '';
          self.dataset.cartHandles = freshRecs.dataset.cartHandles || '';
          self.dataset.baseUrl = freshRecs.dataset.baseUrl || '';
          self.dataset.url = freshRecs.dataset.url || '';

          // Find genuinely new product IDs not in the old set
          var oldIdSet = {};
          oldIds.forEach(function(id) { oldIdSet[id] = true; });
          var brandNewIds = newIds.filter(function(id) { return !oldIdSet[id]; });
          // Deduplicate
          var seen = {};
          brandNewIds = brandNewIds.filter(function(id) {
            if (seen[id]) return false;
            seen[id] = true;
            return true;
          });

          freshRecs.parentNode.replaceChild(self, freshRecs);

          if (brandNewIds.length > 0) {
            // Fetch ONLY for new IDs and merge with existing cards
            self._fetchAndMergeNew(brandNewIds);
          } else {
            // No new products — just sync ADDED states
            self._syncAddedStates();
          }
        } else if (!freshRecs) {
          var cartItems = body.querySelector('cart-drawer-items');
          if (cartItems && cartItems.parentNode) {
            cartItems.parentNode.insertBefore(self, cartItems.nextSibling);
          }
          self._syncAddedStates();
        }

        ProductRecommendations._savedInstance = null;

        // Re-bind quick-cart triggers
        var qcd = document.querySelector('quick-cart-drawer');
        if (qcd && qcd._bindNewTriggers) qcd._bindNewTriggers();
      });
    }

    /**
     * Swap the section title between primary ("COMPLETE THE LOOK") and fallback ("WE THINK YOU'LL LOVE").
     */
    _setTitle(isFallback) {
      var titleEl = this.querySelector('.product-recommendations__title');
      if (!titleEl) return;
      var target = isFallback
        ? (this.dataset.fallbackTitle || '')
        : (this.dataset.primaryTitle || '');
      if (target) titleEl.textContent = target;
    }

    /**
     * Perform a fallback fetch using intent=related instead of intent=complementary.
     * Called when the complementary fetch returns 0 cards.
     */
    _performFallbackRecommendations() {
      var self = this;
      var recommendations = this.querySelector('[data-recommendations]');
      if (!recommendations) return;

      var allProductIds = this.dataset.allProductIds;
      var baseUrl = this.dataset.baseUrl;

      // Replace intent=complementary with intent=related in URLs
      var fallbackBaseUrl = baseUrl ? baseUrl.replace('intent=complementary', 'intent=related') : null;
      var fallbackUrl = this.dataset.url ? this.dataset.url.replace('intent=complementary', 'intent=related') : null;

      if (allProductIds && fallbackBaseUrl) {
        var ids = allProductIds.split(',').filter(Boolean);
        var uniqueIds = ids.filter(function(id, i) { return ids.indexOf(id) === i; });

        var fetches = uniqueIds.map(function(id) {
          var url = fallbackBaseUrl + '&product_id=' + id;
          return fetch(url)
            .then(function(r) { return r.text(); })
            .then(function(text) {
              var doc = new DOMParser().parseFromString(text, 'text/html');
              var container = doc.querySelector('[data-recommendations]');
              if (!container) return [];
              return Array.from(container.querySelectorAll('.product-recommendations__card'));
            })
            .catch(function() { return []; });
        });

        Promise.all(fetches).then(function(results) {
          var seenHandles = {};
          var allCards = [];
          results.forEach(function(cards) {
            cards.forEach(function(card) {
              var handle = card.getAttribute('data-product-handle');
              if (!handle || seenHandles[handle]) return;
              seenHandles[handle] = true;
              allCards.push(card);
            });
          });

          if (allCards.length === 0) return;

          self._setTitle(true);
          self.classList.remove('hidden');
          recommendations.innerHTML = '';
          allCards.forEach(function(card) { recommendations.appendChild(card); });
          self._loaded = true;
          self.initSlider();
          self._syncAddedStates();

          setTimeout(function() {
            var qcd = document.querySelector('quick-cart-drawer');
            if (qcd && qcd.init) qcd.init();
          }, 500);
        }).catch(function() {});

        return;
      }

      // Single product fallback
      if (fallbackUrl) {
        fetch(fallbackUrl)
          .then(function(r) { return r.text(); })
          .then(function(text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var recEl = doc.querySelector('[data-recommendations]');
            var html = recEl ? recEl.innerHTML : '';
            if (!html || !html.trim()) return;

            self._setTitle(true);
            self.classList.remove('hidden');
            recommendations.innerHTML = html;
            self._loaded = true;
            self.initSlider();
            self._syncAddedStates();

            setTimeout(function() {
              var qcd = document.querySelector('quick-cart-drawer');
              if (qcd && qcd.init) qcd.init();
            }, 500);
          })
          .catch(function() {});
      }
    }

    /**
     * Primary fetch — identical to the original theme approach.
     * Fetches data-url, parses [data-recommendations] innerHTML, injects cards.
     */
    performRecommendations() {
      const recommendations = this.querySelector('[data-recommendations]');
      if (!recommendations) {
        return;
      }

      const allProductIds = this.dataset.allProductIds;
      const baseUrl = this.dataset.baseUrl;
      const cartHandles = (this.dataset.cartHandles || '').split(',').filter(Boolean);
      const hasFallback = !!this.dataset.fallbackTitle;

      // If we have multiple product IDs and a base URL, fetch for all and merge
      if (allProductIds && baseUrl) {
        const ids = allProductIds.split(',').filter(Boolean);
        // Deduplicate IDs
        const uniqueIds = ids.filter(function(id, i) { return ids.indexOf(id) === i; });


        const fetches = uniqueIds.map(function(id) {
          const url = baseUrl + '&product_id=' + id;
          return fetch(url)
            .then(function(r) { return r.text(); })
            .then(function(text) {
              var doc = new DOMParser().parseFromString(text, 'text/html');
              var container = doc.querySelector('[data-recommendations]');
              if (!container) return [];
              return Array.from(container.querySelectorAll('.product-recommendations__card'));
            })
            .catch(function() { return []; });
        });

        Promise.all(fetches).then(function(results) {
          var seenHandles = {};
          var allCards = [];

          results.forEach(function(cards) {
            cards.forEach(function(card) {
              var handle = card.getAttribute('data-product-handle');
              if (!handle) return;
              // Skip duplicates (same product recommended by multiple cart items)
              if (seenHandles[handle]) return;
              seenHandles[handle] = true;
              allCards.push(card);
            });
          });

          if (allCards.length === 0) {
            // No complementary products — try fallback if in cart context
            if (hasFallback) {
              this._performFallbackRecommendations();
            }
            return;
          }

          this._setTitle(false);
          this.classList.remove('hidden');
          recommendations.innerHTML = '';
          allCards.forEach(function(card) {
            recommendations.appendChild(card);
          });
          this._loaded = true;
          this.initSlider();
          this._syncAddedStates();

          setTimeout(function() {
            if (document.querySelector('quick-cart-drawer')) {
              document.querySelector('quick-cart-drawer').init();
            }
          }, 500);
        }.bind(this))
        .catch(function(err) {
        });

        return;
      }

      // Fallback: single product ID fetch (PDP context or single cart item)
      const url = this.dataset.url;
      if (!url) {
        return;
      }

      fetch(url)
        .then(response => {
          return response.text();
        })
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const recommendationsEl = doc.querySelector('[data-recommendations]');
          const recommendationsHTML = recommendationsEl?.innerHTML;

          if (!recommendationsHTML || !recommendationsHTML.trim()) {
            // No complementary products — try fallback if in cart context
            if (hasFallback) {
              this._performFallbackRecommendations();
            }
            return;
          }

          this._setTitle(false);
          this.classList.remove('hidden');
          recommendations.innerHTML = recommendationsHTML;
          this._loaded = true;
          this.initSlider();
          this._syncAddedStates();

          setTimeout(() => {
            if (document.querySelector('quick-cart-drawer')) {
              document.querySelector('quick-cart-drawer').init();
            } else {
              this.querySelectorAll('.quick-cart-drawer__trigger').forEach(el => {
                el?.remove();
              });
            }
          }, 500);
        })
        .catch(function(err) {
        });
    }

    /**
     * Fetch recommendations for new product IDs only and merge with existing cards.
     * Preserves all current cards (including ones now in cart) and adds new unique ones.
     */
    _fetchAndMergeNew(newIds) {
      var self = this;
      var baseUrl = this.dataset.baseUrl;
      if (!baseUrl || !newIds.length) {
        this._syncAddedStates();
        return;
      }

      var recommendations = this.querySelector('[data-recommendations]');
      if (!recommendations) return;

      // Collect existing card handles
      var existingHandles = {};
      recommendations.querySelectorAll('.product-recommendations__card').forEach(function(card) {
        var h = card.getAttribute('data-product-handle');
        if (h) existingHandles[h] = true;
      });

      var fetches = newIds.map(function(id) {
        var url = baseUrl + '&product_id=' + id;
        return fetch(url)
          .then(function(r) { return r.text(); })
          .then(function(text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var container = doc.querySelector('[data-recommendations]');
            if (!container) return [];
            return Array.from(container.querySelectorAll('.product-recommendations__card'));
          })
          .catch(function() { return []; });
      });

      Promise.all(fetches).then(function(results) {
        var addedNew = false;
        results.forEach(function(cards) {
          cards.forEach(function(card) {
            var handle = card.getAttribute('data-product-handle');
            if (!handle || existingHandles[handle]) return;
            existingHandles[handle] = true;
            recommendations.appendChild(card);
            addedNew = true;
          });
        });

        // Reinit slider if new cards were added
        if (addedNew && self.slider) {
          try { self.slider.update(); } catch(e) {}
        }

        self._syncAddedStates();

        // Re-bind triggers for new cards
        setTimeout(function() {
          var qcd = document.querySelector('quick-cart-drawer');
          if (qcd && qcd._bindNewTriggers) qcd._bindNewTriggers();
          if (qcd && qcd.init) qcd.init();
        }, 300);
      }).catch(function() {
        self._syncAddedStates();
      });
    }

    /**
     * Sync ADDED labels based on current cart contents.
     */
    _syncAddedStates() {
      var self = this;
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var handles = {};
          (cart.items || []).forEach(function(item) {
            handles[item.handle] = true;
          });

          self.querySelectorAll('.product-recommendations__card').forEach(function(card) {
            var handle = card.getAttribute('data-product-handle');
            var addBtn = card.querySelector('.product-recommendations__card-add');
            if (!addBtn) return;

            if (handles[handle]) {
              addBtn.classList.add('is-added');
              addBtn.innerHTML = '<span>ADDED</span>';
              addBtn.disabled = true;
            } else {
              addBtn.classList.remove('is-added');
              addBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
              addBtn.disabled = false;
            }
          });

          // Re-bind triggers after state change
          var qcd = document.querySelector('quick-cart-drawer');
          if (qcd && qcd._bindNewTriggers) qcd._bindNewTriggers();
        })
        .catch(function() {});
    }

    initSlider() {
      try {
        this.slider = new Swiper(this.querySelector('.swiper'), {
          slidesPerView: 'auto',
          spaceBetween: 16,
        });
      } catch(e) {
      }
    }

    /**
     * Open quick-cart modal when a card's + button is tapped.
     */
    handleCardQuickAdd(triggerBtn) {
      var qcd = document.querySelector('quick-cart-drawer');
      if (!qcd) return;
      if (qcd.fetchProductForQuickCartDrawer) {
        qcd.fetchProductForQuickCartDrawer({ target: triggerBtn, _forceModal: true });
      } else if (qcd.open) {
        qcd.open(triggerBtn.dataset.productUrl);
      }
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
}
